import * as React from "react";
import axios from "axios";
import {flashError} from "components/utils/Flash";
import Table from "react-bootstrap/Table";
import {Component, Item} from "components/models/Order";
import {currencyToNumber, numberToCurrency} from "misc/currency";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";

interface Props {
  orderId: string;
  customerId: string;
  itemId: string;
  costMode: boolean;
}

interface State {
  loading: boolean;
  item?: Item;
  editId?: string;
  editPrice?: string;
  updating: boolean;
  inputItemAdjustment?: string;
  editStockOrder?: Component;
}

export default class extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      loading: true,
      updating: false,
    }
  }

  componentDidMount() {
    this.loadItem();
  }

  loadItem() {
    axios.get(`${this.itemUrl()}.json`)
      .then((resp) => {
        this.setState({
          editId: undefined,
          editPrice: undefined,
          item: resp.data as Item,
          loading: false,
          updating: false,
          inputItemAdjustment: numberToCurrency(resp.data.adjustment.cents / 100)
        });
      })
      .catch(() => {
        flashError('An issue occurred loading the item', 'Error');
      })
  }

  bulkIcon(bulk: boolean) {
    if (bulk) {
      return <i className={"fas fa-fw fa-check text-dark"}/>
    }

    return <i className={"fas fa-fw fa-times text-muted"}/>
  }

  productLink(comp: Component) {
    if (comp.product === null) {
      return <i className="fas fa-times text-muted"></i>
    } else {
      return <a
        target={"_blank"}
        title={comp.product_description}
        href={`/products/${comp.product._id['$oid']}`}
        className="order-link product-url">
        <i className="fas fa-link"></i>
      </a>
    }
  }

  row(comp: Component) {
    const id = comp.id;
    const editing = id === this.state.editId;
    return <tr key={id}>
      <td>{this.productLink(comp)}</td>
      <td>{comp.product_description}</td>
      <td className={"text-right"}>{comp.quantity}</td>
      <td className={"text-right"}>{comp.length.micrometres !== 0 && comp.length_distance}</td>
      <td className={"text-right"}>{comp.width.micrometres !== 0 && comp.width_distance}</td>
      <td
        className={"text-right"}>{(comp.length.micrometres + comp.width.micrometres !== 0) ? (`${comp.square_metre} m2`) : ""}</td>
      <td className={"text-center"}>{this.bulkIcon(comp.bulk_purchase)}</td>
      {this.props.costMode && <td className="text-right">{this.compPrice(comp, comp.product_cost.cents)}</td>}
      <td className={"text-right component-price-r" + (editing ? " editing" : "")}
          onClick={() => {
            if (editing || this.state.updating) {
              return;
            }

            this.setState({editId: id, editPrice: (comp.product_price.cents / 100).toString()}, () => {
              const edit = document.getElementById("editPrice");
              if (!edit) {
                return;
              }
              edit.focus();
              (edit as HTMLInputElement).select();
            })
          }}>
        {this.compPriceElem(editing, comp)}
      </td>
      <td>
        {this.renderStockOrder(comp)}
      </td>
      <td className={"text-right"}>
        <a className={"btn btn-sm btn-light"}
           href={`${this.itemUrl()}/components/${id}/edit`}>
          <i className={"fas fa-edit mr-2"}/>
          Edit
        </a>
      </td>
    </tr>
  }

  renderStockOrder(comp: Component) {
    const {stock_order} = comp;
    if (stock_order) {
      return <>
        {stock_order.po_placed && <><b>Placed: </b>{stock_order.po_placed}<br/></>}
        {stock_order.po_reference && <><b>Ref: </b>{stock_order.po_reference}<br/></>}
        {stock_order.po_received && <><b>Received: </b>{stock_order.po_received}<br/></>}
        <a href="#" onClick={(e) => {
          e.preventDefault();
          this.beginUpdateStockOrder(comp)
        }}>Edit</a>
      </>
    }
    return <a href="#" onClick={(e) => {
      e.preventDefault();
      this.beginUpdateStockOrder(comp)
    }}>Add</a>
  }

  beginUpdateStockOrder(comp: Component) {
    this.setState({editStockOrder: Object.assign({}, comp)})
  }

  updateStockOrder() {
    const comp = this.state.editStockOrder;
    if (!comp || !comp.stock_order) {
      return;
    }
    const url = `${this.itemUrl()}/components/${comp.id}/update_stock_order.json`;
    axios.post(url, {
      po_placed: comp.stock_order.po_placed,
      po_received: comp.stock_order.po_received,
      po_reference: comp.stock_order.po_reference,
      authenticity_token: (window as any)._token
    })
      .then((resp) => {
        this.setState({editStockOrder: undefined});
        this.loadItem();
      })
      .catch(() => {
        flashError('An issue occurred updating the item', 'Error');
      })
  }

  rows(item: Item) {
    if (item.components.length === 0) {
      return (<tr>
        <td colSpan={this.colCount()} className={"text-center"}><a href={`${this.itemUrl()}/components/new`}>Add a
          component</a></td>
      </tr>);
    }
    return item.components.map((c) => this.row(c));
  }

  itemCardHeader(item: Item) {
    return <>
      <div className="card-body">
        <div className="d-flex justify-content-between">
          <div className="align-self-start">
            <table className="mb-0">
              <tbody>
              <tr>
                <td>Name:</td>
                <td className="pl-3">
                  <div className="form-group string optional order_item_name form-group-valid">
                    {this.editItemName(item)}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Adjustment:</td>
                <td className="pl-3">
                  <div className="form-group string optional order_item_adjustment form-group-valid">
                    {this.editItemAdjustment(item)}
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div className="align-self-start ml-2">
            <table className="mb-0">
              <tbody>
              <tr>
                <td className="align-top">Retail:</td>
                <td className="pl-3 field-price">
                  {numberToCurrency(this.state.item!.adjusted_price.cents / 100)}
                </td>
              </tr>
              {this.props.costMode &&
              <tr className="cost-element">
                <td className="align-top">Cost:</td>
                <td className="pl-3 field-cost">
                  {numberToCurrency(this.state.item!.cost.cents / 100)}
                </td>
              </tr>
              }
              </tbody>
            </table>
          </div>
          <div className="align-self-start ml-2">
            <fieldset className="form-group boolean optional order_item_ready_to_fit">
              <div className="form-check">
                {this.editItemReadyToFit(item)}
              </div>
            </fieldset>
            <fieldset className="form-group boolean optional order_item_completed">
              <div className="form-check">
                {this.editItemCompleted(item)}
              </div>
            </fieldset>
          </div>
        </div>
      </div>
    </>
  }

  table() {
    const item = this.state.item!;
    const newComponentPath = `${this.itemUrl()}/components/new`;

    return <>
      {this.itemCardHeader(item)}
      <Table className="padded no-top-border">
        <thead>
        <tr>
          <th className="w10px"></th>
          <th>Product</th>
          <th className="text-right w5p">Quantity</th>
          <th className="text-right w7p">Length</th>
          <th className="text-right w7p">Width</th>
          <th className="text-right w7p">Area</th>
          <th className="w5p">Bulk?</th>
          {this.props.costMode && <th className="text-right w100px">Cost</th>}
          <th className="text-right w100px">Retail</th>
          <th className="text-left w170px">Stock Order</th>
          <th className="w5p"></th>
        </tr>
        </thead>
        <tbody>
        {this.rows(item)}
        </tbody>
        <tfoot>
        <tr>
          <td className="text-right" colSpan={this.colCount()}>
            <a title="Add Component" className="btn btn-sm btn-light py-2" href={newComponentPath}>
              <i className="fas fa-fw fa-plus"></i>
            </a>
          </td>
        </tr>
        </tfoot>
      </Table>
    </>
  }

  colCount() {
    return (this.props.costMode ? 11 : 10);
  }

  loading() {
    return (
      <div className="text-center my-3">
        <i className="fas fa-spinner fa-pulse fa-2x"></i>
      </div>
    )
  }

  render() {
    return (
      <>
        {this.state.loading ? this.loading() : this.table()}
        {this.stockOrderModal()}
      </>
    )
  }

  private itemUrl() {
    return `/customers/${this.props.customerId}/orders/${this.props.orderId}/items/${this.props.itemId}`;
  }

  private compPrice(comp: Component, price = comp.product_price.cents) {
    return <span>{Intl.NumberFormat('en-GB', {
      style: "currency",
      currency: comp.product_price.currency_iso
    }).format(price / 100)}</span>
  }

  private editPrice(comp: Component) {
    return (
      <input type="" id="editPrice" value={this.state.editPrice}
             onKeyPress={(e) => {
               if (e.key === 'Enter') {
                 this.finishEditPrice(comp);
               }
             }}
             onBlur={() => this.finishEditPrice(comp)}
             onChange={(e) => this.setState({editPrice: e.target.value})}/>
    )
  }

  private editItemName(item: Item) {
    return (
      <input
        type="text"
        id={`item_${this.props.itemId}_order_item_name`}
        className="form-control string optional"
        style={{maxWidth: "600px"}}
        name="order_item[name]"
        value={item.name}
        onKeyPress={(e) => {
          if (e.key === 'Enter') this.finishEditItemName(item);
        }}
        onBlur={() => this.finishEditItemName(item)}
        onChange={(e) => {
          const item = this.state.item!;
          item.name = e.target.value;
          this.setState({item: item});
        }}
      />
    )
  }

  private editItemAdjustment(item: Item) {
    return (
      <input
        type="text"
        id={`item_${this.props.itemId}_order_item_adjustment`}
        className="form-control string optional"
        style={{maxWidth: "140px"}}
        name="order_item[adjustment]"
        value={this.state.inputItemAdjustment}
        onKeyPress={(e) => {
          if (e.key === 'Enter') this.finishEditItemAdjustment(item);
        }}
        onBlur={() => this.finishEditItemAdjustment(item)}
        onChange={(e) => this.setState({inputItemAdjustment: e.target.value})}
        onFocus={(e) => {
          let value = e.target.value;
          value = value ? String(currencyToNumber(value)) : '';
          this.setState({inputItemAdjustment: value});
        }}
      />
    )
  }

  private editItemReadyToFit(item: Item) {
    return this.editItemCheckbox(item, "ready_to_fit", "Ready to fit");
  }

  private editItemCompleted(item: Item) {
    return this.editItemCheckbox(item, "completed", "Completed");
  }

  private editItemCheckbox(item: Item, field: string, label: string) {
    return (
      <>
        <input
          id={`foo-item_${this.props.itemId}_order_item_${field}`}
          className="form-check-input boolean optional"
          name={`order_item[${field}]`}
          type="checkbox"
          checked={this.state.item![field]}
          onChange={(e) => {
            const item = this.state.item!;
            item[field] = !item[field];
            this.setState({item: item});
            axios.post(`${this.itemUrl()}/update_${field}.json`, {
              order_item: {[field]: item[field]},
              authenticity_token: (window as any)._token
            });
          }}
        />
        <label
          htmlFor={`foo-item_${this.props.itemId}_order_item_${field}`}
          className="form-check-label boolean optional"
        >{label}</label>
      </>
    )
  }

  private centsToCurrency(cents: string) {
    return numberToCurrency(parseInt(cents) / 100);
  }

  private updateCurrency(className: string, value: string) {
    const asCurrency = this.centsToCurrency(value);
    document.getElementsByClassName(className)[0].innerHTML = asCurrency;
  }

  private finishEditPrice(comp: Component) {
    {
      const val = parseFloat(this.state.editPrice!);
      if (isNaN(val) || (val * 100) === comp.product_price.cents) {
        this.setState({editPrice: undefined, editId: undefined})
        return;
      }
      this.setState({updating: true}, () => {
        axios.post(`${this.itemUrl()}/components/${comp.id}/update_price.json`, {
          order_item_component: {product_price: val},
          authenticity_token: (window as any)._token
        })
          .then((resp) => {
            this.updateOrderPrices(resp);
            this.loadItem();
          })
          .catch(() => {
            flashError('An issue occurred loading the item', 'Error');
          })
      })
    }
  }

  private updateOrderPrices(resp: any) {
    this.updateCurrency('order-adjusted-price', resp.data.order.adjusted_price.cents);
    this.updateCurrency('order-discount', resp.data.order.discount.cents);
    this.updateCurrency('order-total-price', resp.data.order.total_price.cents);
    this.updateCurrency('order-vat', resp.data.order.vat.cents);
  }

  private finishEditItemName(item: Item) {
    axios.post(`${this.itemUrl()}/update_name.json`, {
      order_item: {name: item.name},
      authenticity_token: (window as any)._token
    })
  }

  private finishEditItemAdjustment(item: Item) {
    const val = parseFloat(this.state.inputItemAdjustment!);
    if (isNaN(val) || (val * 100) === item.adjustment.cents) {
      this.setState({inputItemAdjustment: numberToCurrency(item.adjustment.cents / 100)})
      return;
    }
    axios.post(`${this.itemUrl()}/update_adjustment.json`, {
      order_item: {adjustment: val},
      authenticity_token: (window as any)._token
    }).then((resp) => {
      this.updateOrderPrices(resp);
      this.loadItem();
    })
  }

  private compPriceElem(editing: boolean, comp: Component) {
    if (this.state.updating && editing) {
      return "..."
    }
    return editing ? this.editPrice(comp) : this.compPrice(comp)
  }

  private stockOrderModal() {
    const comp = this.state.editStockOrder;
    if (comp && !comp.stock_order) {
      comp.stock_order = {};
    }
    return <Modal show={!!comp} backdropClassName="static"
                  onHide={() => this.setState({editStockOrder: undefined})}>
      <Modal.Header closeButton>
        <Modal.Title>Stock Order for {this.state.editStockOrder?.product?.name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={(e) => {
          e.preventDefault();
          this.updateStockOrder();
        }}>
          <Form.Group className="mb-3">
            <Form.Label>PO Placed</Form.Label>
            <Form.Control type="date" value={comp?.stock_order?.po_placed || ""}
                          onChange={(e) => this.setState((s) => {
                            if (s.editStockOrder) {
                              s.editStockOrder.stock_order = Object.assign({}, s.editStockOrder?.stock_order, {po_placed: e.target.value});
                            }
                            return ({editStockOrder: s.editStockOrder})
                          })}/>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>PO Reference</Form.Label>
            <Form.Control type="text" value={comp?.stock_order?.po_reference || ""}
                          onChange={(e) => this.setState((s) => {
                            if (s.editStockOrder) {
                              s.editStockOrder.stock_order = Object.assign({}, s.editStockOrder?.stock_order, {po_reference: e.target.value});
                            }
                            return ({editStockOrder: s.editStockOrder})
                          })}/>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>PO Received</Form.Label>
            <Form.Control type="date" value={comp?.stock_order?.po_received || ""}
                          onChange={(e) => this.setState((s) => {
                            if (s.editStockOrder) {
                              s.editStockOrder.stock_order = Object.assign({}, s.editStockOrder?.stock_order, {po_received: e.target.value});
                            }
                            return ({editStockOrder: s.editStockOrder})
                          })}/>
          </Form.Group>

        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => this.setState({editStockOrder: undefined})}>
          Close
        </Button>
        <Button variant="primary" onClick={() => this.updateStockOrder()}>Save changes</Button>
      </Modal.Footer>
    </Modal>
  }

}
